// src/pages/payment/PaymentsLedgerPage.tsx
// Payments ledger: every charge/authorize/refund/void across the platform,
// filterable by status and operation, with a detail rail for the full
// attempt timeline and the capture/void/refund actions available on it.
//
// Covers: ListPayments, GetPayment, ChargePayment, AuthorizePayment,
// CapturePayment, VoidPayment, RefundPayment.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { usePayment, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';
import { SplitView } from '@/components/SplitView';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { Payment } from '@core/types/payment';
import { PaymentStatusValues, PaymentOperationTypeValues } from '@core/types/payment';
import { formatDateTime, formatMoney, humanizeEnum, errMessage } from '@/features/payment/format';
import { mapPaymentEntryStatusToVariant, mapPaymentStatusToVariant } from '@/features/payment/mapStatusToVariant';
import { NewPaymentModal, type NewPaymentValue } from '@/features/payment/NewPaymentModal';
import { RefundModal } from '@/features/payment/RefundModal';

const STATUS_OPTIONS = PaymentStatusValues.filter((v) => v !== 'PAYMENT_STATUS_UNSPECIFIED');
const OPERATION_OPTIONS = PaymentOperationTypeValues.filter((v) => v !== 'PAYMENT_OPERATION_TYPE_UNSPECIFIED');

export const PaymentsLedgerPage = observer(() => {
  const store = usePayment();
  const { t } = useI18n();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [operationFilter, setOperationFilter] = useState<string>('');
  const [selected, setSelected] = useState<Payment | null>(null);
  const [newOpen, setNewOpen] = useState<'charge' | 'authorize' | null>(null);
  const [refundOpen, setRefundOpen] = useState(false);
  const [voidTarget, setVoidTarget] = useState<Payment | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = () => {
    store.ListPayments({
      pagination: { cursor: '', limit: 50, direction: 'forward' },
      sort: [], date_range: {} as any, filters: {} as any,
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(operationFilter ? { operation: operationFilter } : {}),
    } as any);
  };

  useEffect(() => { load(); }, [statusFilter, operationFilter]);

  const openDetail = async (p: Payment) => {
    setSelected(p);
    if (p.entity?.id) {
      try {
        const full = await store.GetPayment(p.entity.id);
        setSelected(full);
      } catch {
        // keep list-row version
      }
    }
  };

  const handleNewPayment = async (value: NewPaymentValue) => {
    const payload = {
      entity: {} as any,
      billing_account: { id: value.billing_account_id } as any,
      payment_method_id: value.payment_method_id,
      amount: { amount: Math.round(Number(value.amount) * 100), currency: `CURRENCY_${value.currency.toUpperCase()}` as any, decimal_places: 2 },
      description: value.description,
      idempotency_key: crypto.randomUUID(),
      metadata: {},
    };
    if (newOpen === 'charge') {
      await store.ChargePayment(payload as any);
    } else {
      await store.AuthorizePayment(payload as any);
    }
    setNewOpen(null);
    load();
  };

  const handleCapture = async () => {
    if (!selected?.entity?.id) return;
    setBusy(true);
    setActionError(null);
    try {
      const res = await store.CapturePayment(selected.entity.id, {} as any);
      setSelected(res.payment ?? null);
      load();
    } catch (err) {
      setActionError(errMessage(err, t('payment.ledger.actionError')));
    } finally {
      setBusy(false);
    }
  };

  const handleVoid = async () => {
    if (!voidTarget?.entity?.id) return;
    setBusy(true);
    setActionError(null);
    try {
      const res = await store.VoidPayment(voidTarget.entity.id, { reason: t('payment.ledger.voidReason') } as any);
      setSelected(res.payment ?? null);
      setVoidTarget(null);
      load();
    } catch (err) {
      setActionError(errMessage(err, t('payment.ledger.actionError')));
    } finally {
      setBusy(false);
    }
  };

  const handleRefund = async (value: { full: boolean; amountCents: string; reason: string }) => {
    if (!selected?.entity?.id) return;
    const payload: any = { reason: value.reason, idempotency_key: crypto.randomUUID(), metadata: {} };
    if (!value.full) {
      payload.amount = { amount: Math.round(Number(value.amountCents) * 100), currency: selected.amount?.currency ?? 'CURRENCY_USD', decimal_places: 2 };
    }
    const res = await store.RefundPayment(selected.entity.id, payload);
    setSelected(res.refund_payment ?? null);
    setRefundOpen(false);
    load();
  };

  const list = (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{t('payment.ledger.title')}</span>
          </nav>
          <h1 className="text-2xl font-bold text-foreground">{t('payment.ledger.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t('payment.ledger.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setNewOpen('authorize')}>{t('payment.ledger.newAuthorize')}</Button>
          <Button onClick={() => setNewOpen('charge')}>{t('payment.ledger.newCharge')}</Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 flex flex-col sm:flex-row gap-4">
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} placeholder={t('payment.ledger.allStatuses')} className="sm:w-56" aria-label={t('common.status')}>
            <option value="">{t('payment.ledger.allStatuses')}</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{humanizeEnum(s, 'PAYMENT_STATUS_')}</option>
            ))}
          </Select>
          <Select value={operationFilter} onChange={(e) => setOperationFilter(e.target.value)} placeholder={t('payment.ledger.allOperations')} className="sm:w-56" aria-label={t('payment.ledger.operation')}>
            <option value="">{t('payment.ledger.allOperations')}</option>
            {OPERATION_OPTIONS.map((o) => (
              <option key={o} value={o}>{humanizeEnum(o, 'PAYMENT_OPERATION_TYPE_')}</option>
            ))}
          </Select>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common.description')}</TableHead>
              <TableHead>{t('payment.ledger.operation')}</TableHead>
              <TableHead>{t('common.status')}</TableHead>
              <TableHead className="text-right">{t('payment.ledger.amount')}</TableHead>
              <TableHead>{t('payment.common.created')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={5} message={t('payment.ledger.empty')} />
            ) : (
              store.items.map((p) => (
                <TableRow key={p.entity?.id} className="cursor-pointer" onClick={() => openDetail(p)}>
                  <TableCell>{p.description || p.reference_id || '--'}</TableCell>
                  <TableCell>{humanizeEnum(p.operation, 'PAYMENT_OPERATION_TYPE_')}</TableCell>
                  <TableCell><Badge variant={mapPaymentEntryStatusToVariant(p.status)} size="sm">{humanizeEnum(p.status, 'PAYMENT_STATUS_')}</Badge></TableCell>
                  <TableCell className="text-right">{formatMoney(p.amount)}</TableCell>
                  <TableCell>{formatDateTime(p.created_at)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );

  const detail = selected ? (
    <div className="p-6 space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-foreground">{selected.description || t('payment.ledger.title')}</h2>
        <p className="text-sm text-muted-foreground">{selected.reference_id}</p>
      </div>

      {actionError && <p className="text-sm text-destructive">{actionError}</p>}

      <div className="flex flex-wrap gap-2">
        <Badge variant={mapPaymentEntryStatusToVariant(selected.status)} size="sm">{humanizeEnum(selected.status, 'PAYMENT_STATUS_')}</Badge>
        <Badge variant="secondary" size="sm">{humanizeEnum(selected.operation, 'PAYMENT_OPERATION_TYPE_')}</Badge>
      </div>

      <dl className="divide-y divide-border text-sm">
        <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('payment.ledger.amount')}</dt><dd className="col-span-2">{formatMoney(selected.amount)}</dd></div>
        <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('payment.ledger.captured')}</dt><dd className="col-span-2">{formatMoney(selected.captured_amount)}</dd></div>
        <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('payment.ledger.refunded')}</dt><dd className="col-span-2">{formatMoney(selected.refunded_amount)}</dd></div>
        <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('payment.common.created')}</dt><dd className="col-span-2">{formatDateTime(selected.created_at)}</dd></div>
        {selected.failure_code && (
          <div className="py-2 grid grid-cols-3 gap-2"><dt className="text-muted-foreground">{t('payment.ledger.failure')}</dt><dd className="col-span-2 text-destructive">{selected.failure_code}: {selected.failure_message}</dd></div>
        )}
      </dl>

      <div className="flex flex-wrap gap-2">
        {selected.status === 'PAYMENT_STATUS_AUTHORIZED' && (
          <>
            <Button size="sm" onClick={handleCapture} isLoading={busy}>{t('payment.ledger.capture')}</Button>
            <Button size="sm" variant="secondary" onClick={() => setVoidTarget(selected)}>{t('payment.ledger.void')}</Button>
          </>
        )}
        {(selected.status === 'PAYMENT_STATUS_CAPTURED' || selected.status === 'PAYMENT_STATUS_SETTLED' || selected.status === 'PAYMENT_STATUS_PARTIALLY_REFUNDED') && (
          <Button size="sm" variant="secondary" onClick={() => setRefundOpen(true)}>{t('payment.ledger.refund')}</Button>
        )}
      </div>

      {/* Attempt timeline */}
      <div>
        <h3 className="text-sm font-medium text-foreground mb-2">{t('payment.ledger.attempts')}</h3>
        {selected.attempts?.attempts?.length ? (
          <ol className="space-y-2">
            {selected.attempts.attempts.map((a) => (
              <li key={a.id} className="rounded-md border border-border p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-foreground">{t('payment.ledger.attemptNumber', { n: a.sequence })}</span>
                  <Badge variant={mapPaymentStatusToVariant(a.status!)} size="sm">{humanizeEnum(a.status, 'PAYMENT_ATTEMPT_STATUS_')}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{formatDateTime(a.created_at)} · {formatMoney(a.amount)}</p>
                {a.decline_message && <p className="text-xs text-destructive mt-1">{a.decline_message}</p>}
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-muted-foreground">{t('payment.ledger.noAttempts')}</p>
        )}
      </div>
    </div>
  ) : null;

  return (
    <>
      <SplitView detail={detail} onClose={() => setSelected(null)}>
        {list}
      </SplitView>

      <NewPaymentModal
        open={!!newOpen}
        mode={newOpen ?? 'charge'}
        isSaving={store.isLoading}
        onCancel={() => setNewOpen(null)}
        onSubmit={handleNewPayment}
      />

      <RefundModal
        open={refundOpen}
        maxAmount={selected?.amount}
        isSaving={store.isLoading}
        onCancel={() => setRefundOpen(false)}
        onSubmit={handleRefund}
      />

      <ConfirmDialog
        open={!!voidTarget}
        title={t('payment.ledger.voidConfirmTitle')}
        destructive
        isConfirming={busy}
        onConfirm={handleVoid}
        onCancel={() => setVoidTarget(null)}
      />
    </>
  );
});

export default PaymentsLedgerPage;
